// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Claimant, WatchListThreshold, WatchListRow } from "@/types";
import { Identifier } from "react-admin";
import {
  getEffectiveDeductible,
  getResolvedClaimsValues,
  isLaseredDeductible,
} from "@/resources/claimants/hooks/useCurrentPolicyData";

export interface CalculateWatchListParams {
  claimants: Claimant[];
  policyId: Identifier;
  specificDeductible: number;
}

/** Maps a percentage to the appropriate threshold label */
const getThresholdStatus = (percent: number): WatchListThreshold => {
  if (percent >= 90) return "Approaching";
  if (percent >= 75) return "75% Notice";
  return "50% Notice";
};

/**
 * Calculates watch list rows for claimants between 50–99% of their specific deductible.
 *
 * Uses the backend-computed snapshot total_paid (which deduplicates claim items)
 * rather than re-summing raw claim items, which may include duplicates from
 * multiple document loads.
 */
export const calculateWatchListRows = ({
  claimants,
  policyId,
  specificDeductible,
}: CalculateWatchListParams): WatchListRow[] => {
  const policyIdStr = String(policyId);

  // The base arm of the precedence chain, built once. `specificDeductible` is
  // already `selectedPolicy.specific_benefit.specific_deductible` (useClaimsSummaryData),
  // i.e. exactly the field the resolver falls back to.
  const baseSource = { specific_benefit: { specific_deductible: specificDeductible } };

  const rows = claimants.map((claimant): WatchListRow | null => {
    // Skip claimants not linked to this policy
    const policyData = claimant.policy_data?.[policyIdStr];
    if (!policyData) return null;

    // Use the authoritative snapshot total_paid (deduped by the backend)
    const totalPaid = getResolvedClaimsValues(policyData)?.total_paid ?? 0;

    // Effective deductible via the SHARED resolver (#6929). Previously this
    // re-derived the precedence inline from `is_lasered` / `lasered_attributes`,
    // which read neither `is_contract_lasered` nor `is_contract_excluded` nor the
    // BE-resolved `effective_specific_deductible` -- so a contract-lasered
    // claimant (#5287) was measured against the policy BASE.
    //
    // This must stay in lockstep with calculateClaimsSummaryRows, which is why
    // both now call the same function: the Watch List owns 50-99% and hands the
    // >=100% population to the Summary. If the two disagreed about the
    // denominator, a claimant could sit in BOTH tables (80% of base, and over
    // their contract laser) or in neither.
    const { amount: claimantDeductible, source, reviewPending } = getEffectiveDeductible(policyData, baseSource);

    // A laser flag with no amount (#5901 split-brain) resolves to BASE inside the
    // resolver rather than to 0, so such a claimant is no longer silently dropped
    // by the guard below.
    // A review decision that chose the base is not a laser (isLaseredDeductible).
    const isLasered = isLaseredDeductible({ source, amount: claimantDeductible }, baseSource);

    // Non-positive covers both an unconfigured deductible and a fully-excluded
    // claimant, whose effective deductible is a legitimate 0 -- no coverage, so
    // nothing to watch. Mirrors the Summary's identical guard.
    if (claimantDeductible <= 0) return null;

    const percent = (totalPaid / claimantDeductible) * 100;

    // Watch list range: 50% inclusive to under 100%
    if (percent < 50 || percent >= 100) return null;

    return {
      id: String(claimant.id),
      name: claimant.claimant_name ?? "Unknown Claimant",
      memberId: claimant.member_id,
      totalYTD: totalPaid,
      specDeductible: claimantDeductible,
      percentOfDeductible: percent,
      remaining: claimantDeductible - totalPaid,
      threshold: getThresholdStatus(percent),
      isLasered,
      isContractLasered: source === "CONTRACT",
      isDeductibleReviewPending: reviewPending,
      policyId: policyIdStr,
    };
  });

  // Return sorted by highest % first
  return rows
    .filter((row): row is WatchListRow => row !== null)
    .sort((a, b) => b.percentOfDeductible - a.percentOfDeductible);
};
